/**
 * Utility functions for error handling and classification
 */

import { ErrorInfo } from 'react';
import { trackError, ErrorSeverity, type ErrorContext } from '../../utils/error-tracking';

/**
 * Check if an error is network-related
 */
export function isNetworkError(error: Error): boolean {
  const networkErrorPatterns = [
    'network',
    'fetch',
    'failed to fetch',
    'timeout',
    'connection',
    'xhr',
    'cors',
    'net::',
    'networkerror',
  ];

  const errorMessage = error.message.toLowerCase();
  return networkErrorPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Check if an error is a chunk load error (common in code-split apps)
 */
export function isChunkLoadError(error: Error): boolean {
  const chunkErrorPatterns = [
    'chunkloaderror',
    'loading chunk',
    'failed to import',
    'dynamically imported module',
    'loading css chunk',
    'loading script failed',
  ];

  const errorMessage = error.message.toLowerCase();
  return chunkErrorPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Check if an error is a permission error
 */
export function isPermissionError(error: Error): boolean {
  const permissionPatterns = [
    'permission',
    'unauthorized',
    'forbidden',
    '403',
    'not allowed',
    'access denied',
  ];

  const errorMessage = error.message.toLowerCase();
  return permissionPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Check if an error is authentication-related
 */
export function isAuthError(error: Error): boolean {
  const authPatterns = [
    'unauthenticated',
    'not authenticated',
    'authentication',
    'auth failed',
    '401',
    'token expired',
    'invalid token',
    'session expired',
  ];

  const errorMessage = error.message.toLowerCase();
  return authPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Get a user-friendly error message
 */
export function getErrorMessage(error: Error | unknown, fallback = 'An unexpected error occurred'): string {
  if (!error) return fallback;

  // Handle Error instances
  if (error instanceof Error) {
    // Special handling for known error types
    if (isNetworkError(error)) {
      return 'Connection error. Please check your internet connection and try again.';
    }

    if (isChunkLoadError(error)) {
      return 'Loading error. Please refresh the page to continue.';
    }

    if (isAuthError(error)) {
      return 'Authentication required. Please sign in to continue.';
    }

    if (isPermissionError(error)) {
      return "You don't have permission to perform this action.";
    }

    // Return the error message if it seems user-friendly
    if (error.message && error.message.length < 100 && !error.message.includes('Error:')) {
      return error.message;
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle objects with message property
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }

  return fallback;
}

/**
 * Classify error severity
 */
export function getErrorSeverity(error: Error): 'low' | 'medium' | 'high' | 'critical' {
  if (isChunkLoadError(error)) return 'low';
  if (isNetworkError(error)) return 'medium';
  if (isPermissionError(error)) return 'medium';
  if (isAuthError(error)) return 'high';

  // Check for critical error patterns
  const criticalPatterns = ['fatal', 'critical', 'crash', 'panic'];
  const errorMessage = error.message.toLowerCase();
  if (criticalPatterns.some(pattern => errorMessage.includes(pattern))) {
    return 'critical';
  }

  return 'medium';
}

/**
 * Determine if error should be reported to error tracking
 */
export function shouldReportError(error: Error): boolean {
  // Don't report network errors in development
  if (process.env.NODE_ENV === 'development' && isNetworkError(error)) {
    return false;
  }

  // Don't report chunk load errors - user can refresh
  if (isChunkLoadError(error)) {
    return false;
  }

  // Don't report ResizeObserver errors (common browser quirk)
  if (error.message.includes('ResizeObserver loop limit exceeded')) {
    return false;
  }

  // Don't report certain console errors
  const ignorePatterns = [
    'non-error promise rejection captured',
    'cancelled',
    'aborted',
  ];

  const errorMessage = error.message.toLowerCase();
  if (ignorePatterns.some(pattern => errorMessage.includes(pattern))) {
    return false;
  }

  return true;
}

/**
 * Retry an async operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
    onRetry?: (attempt: number, error: Error) => void;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    onRetry,
  } = options;

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      if (attempt === maxRetries) {
        throw lastError;
      }

      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      );

      onRetry?.(attempt + 1, lastError);

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Capture error to ClickHouse with error boundary context
 * Extracted from duplicate code across error boundaries
 */
export function captureErrorBoundaryException(
  error: Error,
  errorInfo: ErrorInfo,
  options: {
    level: 'page' | 'section' | 'component';
    retryCount?: number;
    additionalContext?: Record<string, unknown>;
  }
): void {
  const { level, retryCount = 0, additionalContext = {} } = options;

  // Determine severity based on level
  const errorSeverity = level === 'page' ? ErrorSeverity.CRITICAL :
                       level === 'section' ? ErrorSeverity.ERROR :
                       ErrorSeverity.WARNING;

  // Track to ClickHouse with enhanced context
  const context: ErrorContext = {
    component: `ErrorBoundary-${level}`,
    boundary: level,
    componentStack: errorInfo.componentStack,
    retryCount,
    tags: {
      errorBoundary: 'true',
      errorLevel: level,
      retryCount: retryCount.toString(),
    },
    extra: {
      ...additionalContext,
    },
  };

  // Add component stack to error for better tracking
  const enhancedError = new Error(error.message);
  enhancedError.name = error.name;
  enhancedError.stack = `${error.stack}\n\nComponent Stack:\n${errorInfo.componentStack}`;

  trackError(enhancedError, errorSeverity, context);
}

/**
 * Get notification message based on error boundary level
 */
export function getErrorBoundaryNotificationMessage(level: 'page' | 'section' | 'component'): string {
  switch (level) {
    case 'page':
      return 'An error occurred on this page. Please try refreshing.';
    case 'section':
      return 'A section of the page encountered an error.';
    case 'component':
    default:
      return 'Something went wrong. Please try again.';
  }
}